import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Lottie from 'react-lottie';
import PhoneIdentityIcon from 'assets/icons/phone-identity.svg?react';
import DesktopIcon from 'assets/icons/desktop.svg?react';
import useNavigateByIdentificationStatus from 'hooks/useBankIdAccountSelection';
import { useClientQuery } from 'hooks/webapi';
import { Accordion, Button, Link, PortalModal, Typography } from 'components';
import PhoneUploadLottie from 'assets/lottie/phone-upload.json';
import H1 from 'components/H/H1';
import MagicLink from '../IdentificationUploadDesktop/MagicLink/MagicLink';
import MagicLinkModal from '../MagicLinkModal/MagicLinkModal';
import styles from './ManualUploadID.module.scss';
import UploadID from '../IdentificationUploadDesktop/UploadID/UploadID';

const phoneUploadLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: PhoneUploadLottie,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

export default function ManualUploadID() {
  const { t } = useTranslation([
    'manual_upload_id',
    'common',
    'identification_upload',
  ]);
  const [hasAcceptedMagicLink, setHasAcceptedMagicLink] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);

  const { data: client, refetch: refetchClient } = useClientQuery();
  const { navigateByIdentificationStatus } = useNavigateByIdentificationStatus(
    'identificationUpload',
  );

  useEffect(() => {
    async function fetchClientAsync() {
      try {
        await refetchClient();
      } catch {
        /* empty */
      }
    }
    if (!client) {
      fetchClientAsync();
    }
  }, [client, refetchClient]);

  const startMagicLinkFlow = () => {
    setHasAcceptedMagicLink(true);
  };

  const items = useMemo(() => {
    return [
      {
        title: t('magic_link_title'),
        icon: PhoneIdentityIcon,
        content: <MagicLink onClick={startMagicLinkFlow} />,
      },
      {
        title: t('desktop_title'),
        icon: DesktopIcon,
        content: (
          <UploadID
            buttonText={t('common:confirm')}
            onSuccess={navigateByIdentificationStatus}
          />
        ),
      },
    ];
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const renderInstructionModal = useCallback(
    () => (
      <>
        <Lottie options={phoneUploadLottieOptions} />
        <div className="box-border max-w-[342px] p-6">
          <Typography className="box-border mb-9 text-center">
            {t('identification_upload:modal_info')}
          </Typography>
          <Button className="w-full" onClick={handleCloseModal}>
            {t('common:continue')}
          </Button>
        </div>
      </>
    ),
    [handleCloseModal],
  );

  const closeMagicLinkModal = useCallback(() => {
    setHasAcceptedMagicLink(false);
  }, []);

  return (
    <div data-test="identification-upload">
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <H1>{t('title')}</H1>
      <p className="px-7 lg:px-0">{t('info')}</p>
      <Accordion className="my-5" items={items} />

      {/* TODO: add pending state */}
      <Link
        dataTest="skip"
        onClick={navigateByIdentificationStatus}
        className="px-7 lg:px-0"
      >
        {t('common:skip')}
      </Link>

      {/* Modal disabled. TODO: Pending to be used to show messages according to the BE error response  */}
      {isModalOpen && false && (
        <PortalModal
          className={styles.modal}
          renderContent={renderInstructionModal}
        />
      )}

      {hasAcceptedMagicLink && (
        <MagicLinkModal
          onSuccess={navigateByIdentificationStatus}
          onCancel={closeMagicLinkModal}
        />
      )}
    </div>
  );
}
